import * as React from 'react'
import { GitBranch, Loader2, Unlink } from 'lucide-react'
import {
  useGetAuthzMeQuery,
  useGetPlcRepoLinksQuery,
  useUnlinkPlcRepoMutation,
  type PlantController,
  type PlcRepositoryLink
} from '@axilon-apps/domains-ontology-ui-state'
import { classifyPlc } from '@atbash-labs/devops-resource-types'
import { Button } from '../../components/ui/button'
import { ConfirmDialog } from '../ConfigurePage/components/DeleteConfirmDialog'
import { extractErrorMessage } from '../IngestPage/util/extractErrorMessage'

function normalized(value: string | undefined): string {
  return value?.trim().toLowerCase() ?? ''
}

function legacyResourceTypeMatches(link: PlcRepositoryLink, controller: PlantController): boolean {
  const resourceType = normalized(link.resource_type)
  if (!resourceType) return true
  const rawController = controller as unknown as Record<string, unknown>
  const sourceFile =
    typeof rawController['source_file'] === 'string' ? rawController['source_file'] : undefined
  return classifyPlc(controller.vendor, sourceFile) === resourceType
}

/**
 * Prefer the branch-stable graph node id. Legacy claims predate node-id
 * capture, so mirror the eligibility resolver's name + resource-type fallback.
 */
export function findControllerRepoLink(
  links: PlcRepositoryLink[],
  controller: PlantController
): PlcRepositoryLink | undefined {
  const byNodeId = links.find(
    (link) => link.prophet_node_id !== undefined && link.prophet_node_id === controller.id
  )
  if (byNodeId) return byNodeId

  return links.find(
    (link) =>
      !link.prophet_node_id &&
      normalized(link.prophet_name) === normalized(controller.name) &&
      legacyResourceTypeMatches(link, controller)
  )
}

export function PlcRepoLinkSection({
  controller
}: {
  controller: PlantController
}): React.ReactElement {
  const linksQuery = useGetPlcRepoLinksQuery()
  const authzQuery = useGetAuthzMeQuery()
  const [unlinkRepo, unlinkState] = useUnlinkPlcRepoMutation()
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [unlinkError, setUnlinkError] = React.useState('')
  const link = React.useMemo(
    () => findControllerRepoLink(linksQuery.data ?? [], controller),
    [controller, linksQuery.data]
  )
  const canUnlink = authzQuery.data?.permissions.includes('devops-sync/write') === true
  const isInitialLoading = linksQuery.isLoading || (linksQuery.isFetching && !linksQuery.data)

  const handleUnlink = async () => {
    if (!link) return
    setUnlinkError('')
    try {
      await unlinkRepo(link.id).unwrap()
      setConfirmOpen(false)
    } catch (error) {
      setUnlinkError(extractErrorMessage(error, 'Failed to unlink the PLC repository mapping.'))
    }
  }

  return (
    <section>
      <div className="mb-2 flex items-baseline gap-3">
        <h3 className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-ink-3">
          DevOps repository link
        </h3>
        {linksQuery.isFetching && (
          <span className="inline-flex items-center gap-1 text-xs text-ink-3">
            <Loader2 className="size-3 animate-spin" aria-hidden />
            Checking…
          </span>
        )}
      </div>

      <div className="rounded-xl border bg-card p-4 text-card-foreground shadow-sm">
        {isInitialLoading ? (
          <p className="m-0 text-sm text-ink-3">Checking repository link…</p>
        ) : linksQuery.isError ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="m-0 text-sm text-st-down" role="alert">
              Repository link status is unavailable. Prophet couldn't load repository links.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={linksQuery.isFetching}
              onClick={() => {
                void linksQuery.refetch()
              }}
            >
              Retry
            </Button>
          </div>
        ) : link ? (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <span className="grid size-8 shrink-0 place-items-center rounded-md bg-brand-soft text-brand">
                  <GitBranch className="size-4" aria-hidden />
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-ink">Linked</div>
                  <div className="truncate text-xs text-ink-3" title={link.local_file}>
                    {link.local_file}
                  </div>
                </div>
              </div>
              {canUnlink && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-st-down/30 text-st-down hover:bg-st-down-bg"
                  disabled={unlinkState.isLoading}
                  onClick={() => setConfirmOpen(true)}
                >
                  {unlinkState.isLoading ? (
                    <Loader2 className="mr-1.5 size-3.5 animate-spin" aria-hidden />
                  ) : (
                    <Unlink className="mr-1.5 size-3.5" aria-hidden />
                  )}
                  Unlink
                </Button>
              )}
            </div>

            <dl className="grid gap-x-6 gap-y-2 text-xs sm:grid-cols-2">
              <LinkField label="Repository ID" value={link.devops_repo_id} />
              <LinkField label="Machine ID" value={link.devops_machine_id} />
              {link.devops_config_id && (
                <LinkField label="Configuration ID" value={link.devops_config_id} />
              )}
              <LinkField label="Repository file" value={link.local_file} />
            </dl>
            {unlinkError && (
              <p className="m-0 text-xs text-st-down" role="alert">
                {unlinkError}
              </p>
            )}
          </div>
        ) : (
          <p className="m-0 text-sm text-ink-3">
            No DevOps repository or machine link exists for this PLC.
          </p>
        )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={`Unlink ${controller.name}?`}
        description="Removes this PLC's repository and machine mapping from Prophet. The PLC, repository, machine, and prior sync history are preserved; future Update PLC runs require linking it again."
        confirmLabel="Unlink"
        destructive
        busy={unlinkState.isLoading}
        keepOpenOnConfirm
        onConfirm={() => {
          void handleUnlink()
        }}
      />
    </section>
  )
}

function LinkField({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div className="min-w-0">
      <dt className="font-semibold uppercase tracking-[0.04em] text-ink-4">{label}</dt>
      <dd className="m-0 truncate font-mono text-ink-2" title={value}>
        {value}
      </dd>
    </div>
  )
}
